import type { Avatar as AvatarType } from '@jotta/types/Avatar'
import { Avatar } from '@jotta/ui/Avatar'
import { t, Trans } from '@lingui/macro'
import { useBreakpointIndex } from '@jotta/ui/useBreakpointIndex'
import type { PhotosApi } from '@jotta/types/PhotosApi'
import { PlainBrandIcon } from '@jotta/ui/BrandIcon'
import SvgUsersShared from '@jotta/ui/SvgUsersShared'
import SvgLockKey from '@jotta/ui/SvgLockKey'
import { useGetCustomer } from '@jotta/grpc-connect-client/customer'
import Spinner from '@jotta/ui/Spinner'
import { ActionShareAlbum } from '../../PhotoActions/ActionShareAlbum'
import { cn } from '@jotta/utils/css'
import { photoActionsApi } from '../../PhotoActions/PhotoActions.config'
import { Popover, PopoverContent, PopoverTrigger } from '@jotta/ui/Popover'
import { observer } from 'mobx-react-lite'
import type { AlbumInfoStore } from '../../../store/AlbumInfoStore'

export const avatarSize = 36

const Member = observer<{ name?: string; avatar: AvatarType }>(function Member({
  name,
  avatar,
}) {
  return (
    <div className="flex items-center gap-2">
      <Avatar size={avatarSize} avatar={avatar} />
      {name}
    </div>
  )
})

function OwnerBadge({ isShared }: { isShared: boolean }) {
  return (
    <>
      <PlainBrandIcon
        icon={isShared ? SvgUsersShared : SvgLockKey}
        height={16}
      />
      {isShared ? <Trans>Publicly viewable</Trans> : <Trans>Private</Trans>}
    </>
  )
}

function OwnerInfo({ album }: { album: PhotosApi.AlbumInfo }) {
  const { ownerAvatar, owner } = album
  const maxSubscriberAvatarCount = useBreakpointIndex() > 1 ? 4 : 2

  if (!owner || !ownerAvatar) {
    return null
  }

  const subscribers = album.shareInfo?.subscribers || []
  const subscriberCount = subscribers.length
  const backgroundColorToAvoid =
    subscribers.at(maxSubscriberAvatarCount)?.userAvatar?.backgroundColor ||
    ownerAvatar.backgroundColor

  return (
    <div className="flex items-center gap-3">
      <Avatar size={avatarSize} avatar={ownerAvatar} />

      <div className="flex flex-col items-start">
        <div className="body-sm-ln max-w-52 overflow-hidden overflow-ellipsis text-nowrap">
          {owner}
        </div>
        <div className="label-sm text-grey4">
          <Trans>Album owner</Trans>
        </div>
      </div>

      {subscriberCount > 0 && (
        <div className="flex pl-5">
          {subscribers
            .slice(0, maxSubscriberAvatarCount)
            .map(({ userAvatar, username }) => (
              <Avatar
                className="!-ml-2"
                avatar={userAvatar}
                key={username}
                size={avatarSize}
              />
            ))}
          {subscriberCount > maxSubscriberAvatarCount && (
            <Avatar
              className="!-ml-2"
              size={avatarSize}
              avatar={{
                initials: `+${subscriberCount - maxSubscriberAvatarCount}`,
                backgroundColor:
                  subscribers
                    .map(s => s.userAvatar.backgroundColor)
                    .find(bgColor => bgColor != backgroundColorToAvoid) ||
                  backgroundColorToAvoid,
              }}
            />
          )}
        </div>
      )}
    </div>
  )
}

export const AlbumMembers = observer<{ store: AlbumInfoStore }>(
  function AlbumMembers({ store }) {
    const { data: customer } = useGetCustomer()
    const album = store.model
    const { isOwner, owner, isNormalAlbum, isShared } = album

    const ownerName = isOwner ? customer?.fullName : album.owner
    const ownerAvatar: AvatarType | undefined = isOwner
      ? customer?.avatar
      : album.ownerAvatar

    if (!ownerAvatar) {
      return <Spinner />
    }

    const subscribers = album.shareInfo?.subscribers || []
    const subscriberCount = subscribers.length
    return (
      <Popover>
        <PopoverTrigger className="btn btn-outline">
          {isOwner ? (
            <OwnerBadge isShared={isShared} />
          ) : (
            <OwnerInfo album={album} />
          )}
        </PopoverTrigger>
        <PopoverContent side="bottom">
          <div className="flex flex-col gap-2 p-4">
            <strong>{t`AlbumContainer.owner`}</strong>
            <div className="flex items-center gap-4">
              <Member name={ownerName} avatar={ownerAvatar} />
              {isOwner && isNormalAlbum && (
                <ActionShareAlbum
                  shareId={album.shareInfo?.uri}
                  album={album}
                  action={{
                    ...photoActionsApi.SHARE_ALBUM,
                    icon: isShared ? SvgUsersShared : SvgLockKey,
                    label: isShared ? t`Publicly shared` : t`Private`,
                  }}
                  buttonClass={cn('btn btn-outline')}
                  username={owner}
                />
              )}
            </div>
            {subscriberCount > 0 && (
              <strong>{t`AlbumContainer.subscribers`}</strong>
            )}
            {subscribers?.map(({ userFullName, userAvatar }, i) => (
              <Member name={userFullName} avatar={userAvatar} key={i} />
            ))}
          </div>
        </PopoverContent>
      </Popover>
    )
  },
)
